/**
 * The waiver editor.
 *
 * A waiver used to be a link to a PDF: the office could change which file it
 * pointed at and nothing else. This is where the waiver itself gets written —
 * the prose a member reads and the questions they answer, as an ordered list
 * of blocks.
 *
 * Three panes, which is the shape this kind of editor wants: what the waiver
 * is made of, what it will look like, and the settings of the one block you
 * are working on. The middle pane is a preview rather than a working form on
 * purpose — an editor that collects real answers invites someone to sign their
 * own draft, and the thing being checked here is whether the waiver READS
 * right.
 *
 * Saving is deliberately not automatic. Editing the live document changes what
 * the next person to sign is agreeing to, which is not a thing to do on every
 * keystroke.
 */
import { useMemo, useState } from 'react';
import {
  isFieldBlock, isSignatureField, newBlockId, validateDocument, visibleBlocks,
  WAIVER_CONTAINER_STYLES, WAIVER_SIGNATURE_MODES, WAIVER_SIGNER_ROLES,
  WAIVER_VISIBILITY_OPERATORS,
  type WaiverBlock, type WaiverContentBlock, type WaiverDocument, type WaiverFieldBlock,
  type WaiverFieldType, type WaiverVisibilityOperator,
} from '@lcc/shared';
import { CheckField, SelectField, TextAreaField, TextField } from '../components/ui';

/* ------------------------------------------------------------ the palette -- */

/** What each block type is FOR, in the office's words rather than the code's. */
const PALETTE: Array<{ type: 'content' | WaiverFieldType; label: string; hint: string }> = [
  { type: 'content', label: 'Paragraph', hint: 'Text to read — a clause, a heading, an explanation' },
  { type: 'consent', label: 'Agreement', hint: 'A clause they must tick to agree to' },
  { type: 'signature', label: 'Signature', hint: 'Sign by drawing, or by typing a name' },
  { type: 'initials', label: 'Initials', hint: 'A short mark beside one clause' },
  { type: 'text', label: 'Short answer', hint: 'A name, a relationship, a single line' },
  { type: 'long_text', label: 'Long answer', hint: 'Medical conditions, notes' },
  { type: 'date', label: 'Date', hint: 'A date of birth, a date of injury' },
  { type: 'email', label: 'Email', hint: 'An email address' },
  { type: 'phone', label: 'Phone', hint: 'A phone number' },
  { type: 'number', label: 'Number', hint: 'A number' },
  { type: 'dropdown', label: 'Choose one (list)', hint: 'A list of choices' },
  { type: 'radio', label: 'Choose one (buttons)', hint: 'A few choices, all shown' },
  { type: 'checkbox', label: 'Yes / no', hint: 'An optional tick — a photo consent, say' },
];

const OPERATOR_LABELS: Record<WaiverVisibilityOperator, string> = {
  eq: 'is',
  neq: 'is not',
  lt: 'is less than',
  lte: 'is at most',
  gt: 'is more than',
  gte: 'is at least',
  ageLessThan: 'is a date of birth under (years)',
  ageGreaterOrEqual: 'is a date of birth of at least (years)',
};

function typeLabel(type: string): string {
  return PALETTE.find((p) => p.type === type)?.label ?? type;
}

/** A field name from a label: what the office types, made safe to key on. */
function keyFromLabel(label: string, taken: Set<string>): string {
  const base = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
    .slice(0, 40) || 'field';
  if (!taken.has(base)) return base;
  for (let i = 2; i < 200; i++) {
    if (!taken.has(`${base}_${i}`)) return `${base}_${i}`;
  }
  return `${base}_${Date.now()}`;
}

function makeBlock(type: 'content' | WaiverFieldType, taken: Set<string>): WaiverBlock {
  if (type === 'content') {
    return { id: newBlockId(), type: 'content', heading: '', body: '' } as WaiverContentBlock;
  }
  const label = type === 'signature' ? 'Signature'
    : type === 'initials' ? 'Initials'
    : type === 'consent' ? 'I agree to the above'
    : typeLabel(type);
  const block: WaiverFieldBlock = {
    id: newBlockId(),
    type,
    key: keyFromLabel(label, taken),
    label,
    required: type === 'consent' || type === 'signature',
  };
  if (type === 'dropdown' || type === 'radio') block.options = ['Yes', 'No'];
  if (type === 'signature' || type === 'initials') block.signatureMode = 'both';
  return block;
}

/* --------------------------------------------------------------- preview -- */

/**
 * What a signer will see. Read-only: the controls are disabled, and a
 * signature shows as the box it will be rather than a working pad.
 *
 * Conditional blocks are shown according to the *preview answers* below, so
 * the office can check "what does a 14-year-old see" without signing anything.
 */
function Preview(
  { doc, answers, selectedId, onSelect }:
  {
    doc: WaiverDocument;
    answers: Record<string, unknown>;
    selectedId: string | null;
    onSelect: (id: string) => void;
  },
) {
  const shown = visibleBlocks(doc, answers as never);
  const hiddenCount = doc.blocks.length - shown.length;

  return (
    <div className="wed-preview">
      {shown.map((block) => (
        <div
          key={block.id}
          className={`wed-pv${selectedId === block.id ? ' is-selected' : ''}`}
          onClick={() => onSelect(block.id)}
          role="presentation"
        >
          {!isFieldBlock(block) ? (
            <div className={`wed-pv__content wed-pv__content--${block.containerStyle ?? 'none'}`}>
              {block.heading && <h4>{block.heading}</h4>}
              {String(block.body || '').split(/\n{2,}/).filter(Boolean).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
              {!block.heading && !block.body && <p className="muted">Empty paragraph</p>}
            </div>
          ) : block.type === 'consent' || block.type === 'checkbox' ? (
            <label className="wed-pv__consent">
              <input type="checkbox" disabled />
              <span>
                {block.label}{block.required && <b className="req"> *</b>}
                {block.helpText && <span className="wed-pv__help">{block.helpText}</span>}
              </span>
            </label>
          ) : isSignatureField(block) ? (
            <div className="wed-pv__field">
              <span className="wed-pv__label">
                {block.label}{block.required && <b className="req"> *</b>}
              </span>
              {(block.signatureMode ?? 'both') !== 'draw' && (
                <div className="wed-pv__input wed-pv__input--ghost">Type a name…</div>
              )}
              {(block.signatureMode ?? 'both') !== 'type' && (
                <div className={`wed-pv__pad${block.type === 'initials' ? ' is-initials' : ''}`}>
                  Sign here
                </div>
              )}
            </div>
          ) : block.type === 'radio' ? (
            <div className="wed-pv__field">
              <span className="wed-pv__label">
                {block.label}{block.required && <b className="req"> *</b>}
              </span>
              <div className="wed-pv__radios">
                {(block.options ?? []).map((o) => <span key={o} className="wed-pv__radio">{o}</span>)}
              </div>
            </div>
          ) : (
            <div className="wed-pv__field">
              <span className="wed-pv__label">
                {block.label}{block.required && <b className="req"> *</b>}
              </span>
              <div className="wed-pv__input wed-pv__input--ghost">
                {block.type === 'dropdown'
                  ? (block.options ?? []).join(' · ') || 'Choose…'
                  : block.placeholder || `${typeLabel(block.type)}…`}
              </div>
              {block.helpText && <span className="wed-pv__help">{block.helpText}</span>}
            </div>
          )}
        </div>
      ))}

      {shown.length === 0 && (
        <p className="muted small">
          Nothing to show yet. Add a paragraph and a signature from the left.
        </p>
      )}
      {hiddenCount > 0 && (
        <p className="wed-preview__hidden">
          {hiddenCount} block{hiddenCount === 1 ? '' : 's'} hidden by the answers above.
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ properties -- */

function Properties(
  { block, doc, onChange, onDelete }:
  {
    block: WaiverBlock;
    doc: WaiverDocument;
    onChange: (next: WaiverBlock) => void;
    onDelete: () => void;
  },
) {
  const index = doc.blocks.findIndex((b) => b.id === block.id);
  /* A rule may only read a field asked EARLIER — that is the only order a
     member can answer in, so those are the only ones offered. */
  const earlierFields = doc.blocks.slice(0, Math.max(index, 0)).filter(isFieldBlock);
  const rule = block.visibleIf;

  const patch = (bits: Partial<WaiverFieldBlock> & Partial<WaiverContentBlock>) =>
    onChange({ ...block, ...bits } as WaiverBlock);

  return (
    <div className="wed-props">
      <h3 className="wv__h">{typeLabel(block.type)}</h3>

      {!isFieldBlock(block) ? (
        <>
          <TextField
            label="Heading" value={block.heading ?? ''}
            onChange={(v) => patch({ heading: v })}
            hint="Optional — leave empty for a plain paragraph."
          />
          <TextAreaField
            label="Text" value={block.body ?? ''} rows={10}
            onChange={(v) => patch({ body: v })}
            hint="Leave a blank line between paragraphs. No formatting — a waiver reads as
              plain text, which is also what prints on the signed PDF."
          />
          <SelectField
            label="Set off from the text around it" value={block.containerStyle ?? 'none'}
            onChange={(v) => patch({ containerStyle: v as never })}
            options={WAIVER_CONTAINER_STYLES.map((s) => ({
              value: s,
              label: { none: 'Not at all', border: 'In a bordered box',
                shadow: 'In a raised card', filled: 'In a tinted box' }[s],
            }))}
          />
        </>
      ) : (
        <>
          <TextAreaField
            label={block.type === 'consent' ? 'The clause they are agreeing to' : 'Label'}
            value={block.label} rows={block.type === 'consent' ? 4 : 2}
            onChange={(v) => patch({ label: v })}
            hint={block.type === 'consent'
              ? 'Write it as the statement they are agreeing to — it is printed beside '
                + 'their answer on the signed PDF.'
              : 'What the question is called on the form.'}
          />

          <TextField
            label="Field name" value={block.key}
            onChange={(v) => patch({ key: v.replace(/[^a-zA-Z0-9_]/g, '_') })}
            hint="How the answer is stored, and what a rule below points at. Changing it
              on a waiver people have already signed does not change what they signed."
          />

          <CheckField
            label="Required" checked={block.required}
            onChange={(v) => patch({ required: v })}
            hint={block.type === 'consent'
              ? 'They cannot sign without ticking it.'
              : 'They cannot sign without answering.'}
          />

          {(block.type === 'dropdown' || block.type === 'radio') && (
            <TextAreaField
              label="Choices" rows={5} value={(block.options ?? []).join('\n')}
              onChange={(v) => patch({
                options: v.split('\n').map((o) => o.trim()).filter(Boolean),
              })}
              hint="One per line."
            />
          )}

          {isSignatureField(block) && (
            <SelectField
              label="How they sign" value={block.signatureMode ?? 'both'}
              onChange={(v) => patch({ signatureMode: v as never })}
              options={WAIVER_SIGNATURE_MODES.map((m) => ({
                value: m,
                label: { both: 'Draw it or type a name', draw: 'Must be drawn',
                  type: 'Typed name only' }[m],
              }))}
            />
          )}

          {!isSignatureField(block) && block.type !== 'consent' && (
            <TextField
              label="Placeholder" value={block.placeholder ?? ''}
              onChange={(v) => patch({ placeholder: v })}
              hint="Optional grey example text inside the box."
            />
          )}

          <TextField
            label="Help text" value={block.helpText ?? ''}
            onChange={(v) => patch({ helpText: v })}
            hint="Optional note under the field."
          />

          <SelectField
            label="Who fills this in" value={block.signerRole ?? 'participant'}
            onChange={(v) => patch({ signerRole: v as never })}
            options={WAIVER_SIGNER_ROLES.map((r) => ({
              value: r,
              label: r === 'participant' ? 'The participant' : 'Their parent or guardian',
            }))}
          />
        </>
      )}

      {/* ------------------------------------------------- conditional -- */}
      <h3 className="wv__h">Show this only when…</h3>
      {earlierFields.length === 0 ? (
        <p className="small muted">
          Nothing is asked before this block, so there is no answer to key off. Move it
          further down, or add a question above it.
        </p>
      ) : (
        <>
          <SelectField
            label="Depends on" value={rule?.fieldKey ?? ''}
            onChange={(v) => onChange({
              ...block,
              visibleIf: v
                ? { fieldKey: v, operator: rule?.operator ?? 'eq', value: rule?.value ?? '' }
                : undefined,
            } as WaiverBlock)}
            options={[
              { value: '', label: 'Always show it' },
              ...earlierFields.map((f) => ({ value: f.key, label: f.label.slice(0, 60) })),
            ]}
          />
          {rule && (
            <div className="grid grid--2">
              <SelectField
                label="Test" value={rule.operator}
                onChange={(v) => onChange({
                  ...block, visibleIf: { ...rule, operator: v as WaiverVisibilityOperator },
                } as WaiverBlock)}
                options={WAIVER_VISIBILITY_OPERATORS.map((o) => ({
                  value: o, label: OPERATOR_LABELS[o],
                }))}
              />
              <TextField
                label="Value" value={String(rule.value ?? '')}
                onChange={(v) => onChange({
                  ...block,
                  visibleIf: {
                    ...rule,
                    value: /^(ageLessThan|ageGreaterOrEqual)$/.test(rule.operator)
                      ? (Number(v) || 0) : v,
                  },
                } as WaiverBlock)}
                hint={rule.operator.startsWith('age') ? 'In years, e.g. 18.' : undefined}
              />
            </div>
          )}
        </>
      )}

      <div className="row row--tight" style={{ marginTop: '1.25rem' }}>
        <button className="btn btn--danger" type="button" onClick={onDelete}>
          Remove this block
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ the editor -- */

export function WaiverEditor(
  { value, onChange }:
  { value: WaiverDocument | null; onChange: (next: WaiverDocument | null) => void },
) {
  const doc: WaiverDocument = value ?? { blocks: [] };
  const [selectedId, setSelectedId] = useState<string | null>(doc.blocks[0]?.id ?? null);
  const [dragId, setDragId] = useState<string | null>(null);
  /* Answers used only to drive the preview's conditional blocks — a way to ask
     "what does a junior see" without signing anything. */
  const [previewAnswers, setPreviewAnswers] = useState<Record<string, unknown>>({});

  const selected = doc.blocks.find((b) => b.id === selectedId) ?? null;
  const problems = useMemo(
    () => (doc.blocks.length ? validateDocument(doc) : []),
    [doc],
  );
  const takenKeys = useMemo(
    () => new Set(doc.blocks.filter(isFieldBlock).map((b) => b.key)),
    [doc],
  );
  /* Date fields are what an age rule can read, and they are what makes the
     preview worth having: set one and the guardian half appears. */
  const dateFields = doc.blocks.filter(isFieldBlock).filter((b) => b.type === 'date');

  const setBlocks = (blocks: WaiverBlock[]) => onChange({ ...doc, blocks });

  const addBlock = (type: 'content' | WaiverFieldType) => {
    const block = makeBlock(type, takenKeys);
    const at = selectedId ? doc.blocks.findIndex((b) => b.id === selectedId) + 1 : doc.blocks.length;
    const blocks = [...doc.blocks];
    blocks.splice(at, 0, block);
    setBlocks(blocks);
    setSelectedId(block.id);
  };

  const move = (id: string, by: number) => {
    const from = doc.blocks.findIndex((b) => b.id === id);
    const to = from + by;
    if (from < 0 || to < 0 || to >= doc.blocks.length) return;
    const blocks = [...doc.blocks];
    const [block] = blocks.splice(from, 1);
    blocks.splice(to, 0, block!);
    setBlocks(blocks);
  };

  const dropOn = (targetId: string) => {
    if (!dragId || dragId === targetId) return;
    const from = doc.blocks.findIndex((b) => b.id === dragId);
    const to = doc.blocks.findIndex((b) => b.id === targetId);
    if (from < 0 || to < 0) return;
    const blocks = [...doc.blocks];
    const [block] = blocks.splice(from, 1);
    blocks.splice(to, 0, block!);
    setBlocks(blocks);
  };

  if (!value) {
    return (
      <div className="wed-empty">
        <h3>This waiver is a link to a PDF.</h3>
        <p className="small muted">
          Members read the document at its address and sign the whole thing. That still
          works and nothing needs changing. Build it as a document instead and they can
          fill it in online — answering questions, ticking clauses one by one, and signing
          where the waiver asks them to.
        </p>
        <p className="small muted">
          The PDF stays linked either way, so anyone can still read the original.
        </p>
        <button
          className="btn btn--accent" type="button"
          onClick={() => {
            const intro = makeBlock('content', new Set()) as WaiverContentBlock;
            intro.heading = 'About this waiver';
            intro.body = 'Read this carefully before you sign.';
            const sig = makeBlock('signature', new Set()) as WaiverFieldBlock;
            onChange({ blocks: [intro, sig] });
            setSelectedId(intro.id);
          }}
        >
          Build it as a document
        </button>
      </div>
    );
  }

  return (
    <div className="wed">
      {/* ------------------------------------------------- blocks ----- */}
      <div className="wed-col wed-col--blocks">
        <h3 className="wv__h">The waiver</h3>
        <ol className="wed-list">
          {doc.blocks.map((block, i) => (
            <li
              key={block.id}
              draggable
              onDragStart={() => setDragId(block.id)}
              onDragEnd={() => setDragId(null)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => dropOn(block.id)}
              className={
                'wed-item'
                + (selectedId === block.id ? ' is-selected' : '')
                + (dragId === block.id ? ' is-dragging' : '')
              }
            >
              <button type="button" className="wed-item__main" onClick={() => setSelectedId(block.id)}>
                <span className="wed-item__type">{typeLabel(block.type)}</span>
                <span className="wed-item__label">
                  {isFieldBlock(block)
                    ? block.label || '(no label)'
                    : block.heading || block.body?.slice(0, 60) || '(empty)'}
                </span>
                {isFieldBlock(block) && block.required && <span className="wed-item__req">required</span>}
                {block.visibleIf && <span className="wed-item__cond">conditional</span>}
              </button>
              {/* Dragging is the quick way; these are the one that works with a
                  keyboard, and on a touchscreen. */}
              <span className="wed-item__moves">
                <button type="button" aria-label="Move up" disabled={i === 0}
                  onClick={() => move(block.id, -1)}>↑</button>
                <button type="button" aria-label="Move down" disabled={i === doc.blocks.length - 1}
                  onClick={() => move(block.id, 1)}>↓</button>
              </span>
            </li>
          ))}
        </ol>

        <h3 className="wv__h">Add a block</h3>
        <div className="wed-palette">
          {PALETTE.map((p) => (
            <button key={p.type} type="button" className="wed-add"
              onClick={() => addBlock(p.type)} title={p.hint}>
              <span>{p.label}</span>
              <small>{p.hint}</small>
            </button>
          ))}
        </div>

        {problems.length > 0 && (
          <div className="banner banner--warn" style={{ marginTop: '1rem' }}>
            <strong>Not ready to publish.</strong>
            <ul style={{ margin: '.4rem 0 0', paddingLeft: '1.1rem' }}>
              {problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          </div>
        )}
      </div>

      {/* ------------------------------------------------ preview ----- */}
      <div className="wed-col wed-col--preview">
        <h3 className="wv__h">What a member sees</h3>
        {dateFields.length > 0 && (
          <div className="wed-try">
            <span className="wed-try__label">Try it as someone born</span>
            {dateFields.map((f) => (
              <input
                key={f.key} type="date" className="wed-try__date"
                value={String(previewAnswers[f.key] ?? '')}
                onChange={(e) => setPreviewAnswers((a) => ({ ...a, [f.key]: e.target.value }))}
                aria-label={`Preview value for ${f.label}`}
              />
            ))}
            <button type="button" className="btn btn--sm"
              onClick={() => setPreviewAnswers({})}>Reset</button>
          </div>
        )}
        <Preview
          doc={doc} answers={previewAnswers}
          selectedId={selectedId} onSelect={setSelectedId}
        />
      </div>

      {/* --------------------------------------------- properties ----- */}
      <div className="wed-col wed-col--props">
        {selected ? (
          <Properties
            block={selected} doc={doc}
            onChange={(next) => setBlocks(doc.blocks.map((b) => (b.id === next.id ? next : b)))}
            onDelete={() => {
              setBlocks(doc.blocks.filter((b) => b.id !== selected.id));
              setSelectedId(null);
            }}
          />
        ) : (
          <p className="small muted">Choose a block on the left to edit it.</p>
        )}
      </div>
    </div>
  );
}
